import { useMemo } from 'react';
import classNames from 'classnames';
import { Tooltip } from '@crazyegginc/hatch';

import { useNotifications } from '/src/hooks';
import { copyToClipboard } from '/src/utils';
import { isValidUrl } from '/src/utils/url';
import { SupportLinks } from '/src/support';
import { SupportLink } from '/src/features/_global/components/SupportLink';
import { getGoalTriggerDisplayModel } from '/src/features/goals/triggerMetadata';
import { generateCodeGoalScript } from '/src/features/goals/utils';

function CopyCodeLink({ trigger, segment, theme = 'light' }) {
  const notifications = useNotifications();
  const script = useMemo(() => generateCodeGoalScript(trigger, segment, false), [segment, trigger]);

  function handleOnClick() {
    copyToClipboard(script);
    notifications.success({ content: 'Script copied to clipboard', timeout: 2000 });
  }

  return (
    <Tooltip
      interactive={false}
      darkTheme={true}
      placement="bottom"
      tooltipContent={<div className="w-40">Click to copy the script for this trigger</div>}
    >
      <button
        type="button"
        onClick={() => handleOnClick()}
        className={classNames('cursor-pointer', {
          'text-xs text-malibu-500 hover:underline': theme === 'dark',
          'text-dodger-blue-500 text-sm hover:underline': theme === 'light',
        })}
      >
        Copy script
      </button>
    </Tooltip>
  );
}

export function RenderTriggerText({ trigger, theme = 'dark', single = false, isDeleted = false }) {
  const displayModel = getGoalTriggerDisplayModel(trigger);
  const hasCodeValue = displayModel.isCode && !!displayModel.codeValue;
  const urlAddress = typeof displayModel.urlAddress === 'string' ? displayModel.urlAddress.trim() : '';
  const hasUrlAddress = urlAddress.length > 0;
  const hasValidUrlAddress = hasUrlAddress && isValidUrl(urlAddress);

  return (
    <>
      <span
        className={classNames('font-bold', {
          'text-xs mb-1': !single,
          'text-sm mb-0.25': single,
          'text-body-3': isDeleted,
        })}
      >
        {displayModel.title}
        {isDeleted ? ` (deleted)` : ''}
      </span>

      {displayModel.isCode && !isDeleted ? (
        <>
          {hasCodeValue ? (
            <CopyCodeLink trigger={trigger} segment={displayModel.codeValue} theme={theme} />
          ) : (
            <Tooltip
              interactive={true}
              darkTheme={true}
              placement="bottom"
              tooltipContent={
                <div className="w-40">
                  Read our{' '}
                  <SupportLink href={SupportLinks.script.ce2Functions} className="text-link">
                    help article
                  </SupportLink>{' '}
                  on setting up code goals.
                </div>
              }
            >
              <span className="cursor-pointer text-2xs font-normal underline decoration-cadet-blue-500 decoration-dashed underline-offset-2">
                Conversion triggered
              </span>
            </Tooltip>
          )}
        </>
      ) : (
        <span
          className={classNames({
            'text-xs truncate': !single,
            'text-sm': single,
          })}
        >
          {displayModel.text && `${displayModel.text} `}
          {hasValidUrlAddress ? (
            <a
              href={urlAddress}
              className={classNames('hover:underline', {
                'text-malibu-500': theme === 'dark',
                'text-dodger-blue-500': theme === 'light',
              })}
              target="_blank"
              rel="noopener noreferrer"
            >
              {urlAddress}
            </a>
          ) : hasUrlAddress ? (
            <span>{urlAddress}</span>
          ) : null}
        </span>
      )}
    </>
  );
}
